import { useLayoutEffect, useState } from "react";

/**
 * How many items fit on one row. When not all of them fit, room is kept for the "More" button
 * that will hold the rest.
 *
 * @param {number[]} itemWidths - width of each item, in display order
 * @param {number} containerWidth
 * @param {number} moreWidth - width of the "More" button
 * @param {number} [gap] - space between items
 * @returns {number} count of leading items to show
 */
export const countFittingItems = (itemWidths, containerWidth, moreWidth, gap = 0) => {
  const total = itemWidths.reduce((sum, width) => sum + width, 0) + gap * Math.max(0, itemWidths.length - 1);
  if (total <= containerWidth) return itemWidths.length;

  let used = moreWidth;
  let count = 0;
  for (const width of itemWidths) {
    used += width + gap;
    if (used > containerWidth) break;
    count += 1;
  }
  return count;
};

/**
 * Measures a hidden copy of the items and returns how many fit in the container on one row.
 *
 * @param {React.RefObject<HTMLElement>} containerRef - the row that has the available width
 * @param {React.RefObject<HTMLElement>} measureRef - hidden row: every item, then the "More" button
 * @param {number} itemCount
 * @param {number} [gap]
 * @returns {number}
 */
export default function useVisibleCount(containerRef, measureRef, itemCount, gap = 0) {
  const [visibleCount, setVisibleCount] = useState(itemCount);

  useLayoutEffect(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure) return undefined;

    const update = () => {
      const widths = Array.from(measure.children).map((child) => child.getBoundingClientRect().width);
      const moreWidth = widths.pop() || 0;
      setVisibleCount(countFittingItems(widths, container.clientWidth, moreWidth, gap));
    };

    update();
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(update);
    observer.observe(container);
    observer.observe(measure);
    return () => observer.disconnect();
  }, [containerRef, measureRef, itemCount, gap]);

  return visibleCount;
}
